import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { setOrderDiscount, type OrderDiscount } from '../../lib/orders';
import { formatCurrency } from '../../lib/format';
import {
  DISCOUNT_REASON_OPTIONS,
  REASON_NOTE_MAX,
  reasonOption,
  type DiscountReason,
} from '../../lib/discount-constants';

interface KortingPillProps {
  orderId: string;
  discount: OrderDiscount;
  canEdit: boolean;
  /** Gefactureerde order: korting gaat via een creditfactuur, de knop legt dat uit. */
  gefactureerd?: boolean;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

/**
 * Drie invoeren voor dezelfde korting: het percentage (rauw), een bedrag dat
 * eraf gaat ("€ 47,65 korting") en de eindprijs ("we doen het voor € 100").
 * Bedrag en eindprijs zijn twee verschillende getallen; op 02-11 werd € 47,65
 * korting als eindprijs ingevoerd en rekende de knop 92% uit (WB-324879).
 */
type InvoerModus = 'percentage' | 'bedrag' | 'eindprijs';

/**
 * Pill in OrderDetail-header die de actieve order-korting toont en — bij
 * klikken — een mini-popover opent om het percentage te wijzigen of te
 * verwijderen. Campagne-codes zijn read-only (caller moet eerst de campagne
 * wissen).
 */
export function KortingPill({ orderId, discount, canEdit, gefactureerd = false, onSaved }: KortingPillProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isCampaign = discount.kind === 'campaign';
  const isToeslag = discount.kind === 'toeslag' && (discount.percentage ?? 0) > 0;
  const hasActive = (discount.kind === 'manual' || discount.kind === 'toeslag') && (discount.percentage ?? 0) > 0;
  const [mutType, setMutType] = useState<'korting' | 'toeslag'>(isToeslag ? 'toeslag' : 'korting');
  // De server rekent bedrag en eindprijs terug naar een percentage, zodat de
  // medewerker niet zelf door 1,21 en het aantal m² hoeft te delen.
  const [invoerModus, setInvoerModus] = useState<InvoerModus>('percentage');
  // Reden is verplicht bij toepassen — de API weigert zonder. Bewust geen
  // default: een voorgeselecteerde reden is geen geregistreerde reden.
  const [reason, setReason] = useState<DiscountReason | ''>('');
  const [reasonNote, setReasonNote] = useState('');
  // Uitkomst van de laatste opslag: waar het bedrag op landde, en of de
  // betaallink mee kon. Houdt de popover open zolang er iets te melden is.
  const [resultaat, setResultaat] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInput(hasActive ? String(discount.percentage) : '');
    setMutType(isToeslag ? 'toeslag' : 'korting');
    setInvoerModus('percentage');
    setReason('');
    setReasonNote('');
    setError(null);
    setResultaat(null);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, hasActive, isToeslag, discount.percentage]);

  // Pill-styling: campagne moet visueel klikbaar zijn (amber blijft, maar voeg
  // hover-state toe omdat de pill nu open-popover-trigger is i.p.v. read-only).

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /**
   * `waarde` is een percentage, een kortingsbedrag of een eindprijs (incl.
   * BTW), afhankelijk van `modus`. `null` betekent altijd verwijderen.
   */
  const save = async (waarde: number | null, modus: InvoerModus = 'percentage') => {
    if (pending) return;
    // Verwijderen mag zonder reden; toepassen niet.
    if (waarde !== null && !reason) {
      setError('Kies een reden');
      return;
    }
    const selected = reasonOption(reason || null);
    const trimmedNote = reasonNote.trim();
    if (waarde !== null && selected?.noteRequired && !trimmedNote) {
      setError('Licht kort toe waarom');
      return;
    }
    setPending(true);
    setError(null);
    setResultaat(null);
    try {
      const result = await setOrderDiscount(orderId, modus === 'percentage' ? waarde : null, {
        overrideCampaign: isCampaign,
        type: mutType,
        ...(waarde !== null && reason ? { reason } : {}),
        ...(trimmedNote ? { reasonNote: trimmedNote } : {}),
        ...(modus === 'eindprijs' && waarde !== null ? { targetAmountInclBtw: waarde } : {}),
        ...(modus === 'bedrag' && waarde !== null ? { discountAmountInclBtw: waarde } : {}),
      });
      onSaved(result.nieuweLastModifiedDate);

      // De popover sluit alleen als er niets te melden valt. Een bedrag dat
      // niet exact haalbaar was of een betaallink die achterbleef moet de
      // medewerker zien, niet ontdekken op de factuur.
      const meldingen = [
        !result.doelGehaald && result.nieuweTotaalInclBtw != null
          ? result.laagsteHaalbaarInclBtw != null
            ? `Laagste haalbare bedrag is ${formatCurrency(result.laagsteHaalbaarInclBtw)}; de order staat nu op ${formatCurrency(result.nieuweTotaalInclBtw)}.`
            : `De order staat nu op ${formatCurrency(result.nieuweTotaalInclBtw)}.`
          : null,
        result.melding,
      ].filter(Boolean) as string[];

      if (meldingen.length > 0) {
        setResultaat(meldingen.join(' '));
      } else {
        setOpen(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const bedragModus: InvoerModus | null =
      mutType === 'korting' && invoerModus !== 'percentage' ? invoerModus : null;
    const trimmed = input.trim().replace(',', '.');
    if (!trimmed) {
      // leeg veld in submit = verwijderen
      await save(null);
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError('Geef een geldig getal op');
      return;
    }
    if (bedragModus) {
      if (parsed <= 0) {
        setError('Geef een bedrag boven € 0,00 op');
        return;
      }
      await save(parsed, bedragModus);
      return;
    }
    await save(parsed > 0 ? parsed : null);
  };

  const pillClass = clsx(
    'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium border',
    hasActive && isToeslag
      ? 'bg-sky-50 text-sky-700 border-sky-200'
      : hasActive
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : isCampaign
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-slate-50 text-slate-500 border-slate-200',
    canEdit && 'cursor-pointer hover:ring-1 hover:ring-slate-300',
    pending && 'opacity-60'
  );

  const label = (() => {
    if (isToeslag) return `+${discount.percentage}% toeslag`;
    if (hasActive) return `−${discount.percentage}% korting`;
    if (isCampaign) return `Campagne: ${discount.code}`;
    // Bewerkbaar zonder actieve korting → toon als actie, niet als status.
    return canEdit ? '+ Korting/toeslag' : 'Geen korting';
  })();

  if (!canEdit) {
    return (
      <span className={pillClass}>
        {label}
        {discount.totalAmountInclBtw > 0 && hasActive && (
          <span className="text-[10px] opacity-75">
            ({formatCurrency(discount.totalAmountInclBtw)})
          </span>
        )}
      </span>
    );
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        className={pillClass}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {pending && (
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
          </svg>
        )}
        <span>{label}</span>
        {discount.totalAmountInclBtw > 0 && hasActive && (
          <span className="text-[10px] opacity-75">
            ({formatCurrency(discount.totalAmountInclBtw)})
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            {isCampaign && (
              <div className="rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-2 py-1.5">
                <strong>Vervangt campagne:</strong> {discount.code}. De handmatige korting wint na opslaan.
              </div>
            )}
            <div className="flex gap-1">
              {(['korting', 'toeslag'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setMutType(t);
                    // Een toeslag als vast bedrag bestaat niet: dan zou je de
                    // order duurder maken via een kortingsveld.
                    if (t === 'toeslag') setInvoerModus('percentage');
                  }}
                  disabled={pending}
                  className={clsx(
                    'flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors',
                    mutType === t
                      ? t === 'toeslag'
                        ? 'border-sky-500 bg-sky-50 text-sky-700'
                        : 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  )}
                >
                  {t === 'korting' ? 'Korting' : 'Toeslag'}
                </button>
              ))}
            </div>
            {mutType === 'korting' && (
              <div className="flex gap-1">
                {(
                  [
                    { modus: 'percentage', label: 'Percentage' },
                    { modus: 'bedrag', label: 'Bedrag eraf' },
                    { modus: 'eindprijs', label: 'Eindprijs' },
                  ] as const
                ).map((m) => (
                  <button
                    key={m.modus}
                    type="button"
                    onClick={() => {
                      setInvoerModus(m.modus);
                      setInput('');
                      setError(null);
                    }}
                    disabled={pending}
                    className={clsx(
                      'flex-1 px-2 py-1 text-[11px] font-medium rounded border transition-colors',
                      invoerModus === m.modus
                        ? 'border-slate-400 bg-slate-100 text-slate-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            <label className="text-xs font-medium text-slate-700">
              {mutType === 'toeslag'
                ? 'Toeslagpercentage'
                : invoerModus === 'bedrag'
                  ? 'Korting in euro (incl. BTW)'
                  : invoerModus === 'eindprijs'
                    ? 'Afgesproken eindprijs (incl. BTW)'
                    : 'Kortingspercentage'}
            </label>
            <div className="flex items-center gap-2">
              {mutType === 'korting' && invoerModus !== 'percentage' && (
                <span className="text-sm text-slate-500">€</span>
              )}
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="0"
                disabled={pending}
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {!(mutType === 'korting' && invoerModus !== 'percentage') && (
                <span className="text-sm text-slate-500">%</span>
              )}
            </div>
            {mutType === 'korting' && invoerModus === 'bedrag' && (
              <p className="text-[11px] text-slate-400 -mt-1">
                Gaat van de huidige prijs af. Het kortingspercentage wordt erbij gezocht.
              </p>
            )}
            {mutType === 'korting' && invoerModus === 'eindprijs' && (
              <p className="text-[11px] text-slate-400 -mt-1">
                Het bedrag dat de klant uiteindelijk betaalt. Het kortingspercentage wordt
                erbij gezocht.
              </p>
            )}

            <label className="text-xs font-medium text-slate-700">Reden</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as DiscountReason | '')}
              disabled={pending}
              className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Kies een reden…</option>
              {DISCOUNT_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {reasonOption(reason || null) ? (
              <p className="text-[11px] text-slate-400 -mt-1">{reasonOption(reason || null)!.hint}</p>
            ) : null}
            {reason ? (
              <input
                type="text"
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                maxLength={REASON_NOTE_MAX}
                disabled={pending}
                placeholder={
                  reasonOption(reason)?.noteRequired ? 'Toelichting (verplicht)' : 'Toelichting (optioneel)'
                }
                className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ) : null}

            {gefactureerd && (
              <p className="text-xs text-amber-700">
                ⚠ Deze order is al gefactureerd. Korting achteraf gaat via een creditfactuur
                op de factuurpagina; de order zelf blijft zoals gefactureerd.
              </p>
            )}
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={pending || gefactureerd}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-1.5 rounded disabled:opacity-60"
              >
                Opslaan
              </button>
              {hasActive && (
                <button
                  type="button"
                  onClick={() => save(null)}
                  disabled={pending}
                  className="text-xs text-red-600 hover:text-red-700 disabled:opacity-60"
                >
                  Verwijderen
                </button>
              )}
            </div>
            {error && (
              <p className="text-xs text-amber-700">⚠ {error}</p>
            )}
            {resultaat && (
              <div className="rounded bg-slate-50 border border-slate-200 text-slate-700 text-[11px] px-2 py-1.5">
                {resultaat}
              </div>
            )}
            <p className="text-[11px] text-slate-400">
              Prijzen worden automatisch herberekend. Handmatige prijzen (Andere_prijs) blijven
              ongewijzigd, en het bedrag kan daardoor een paar cent van je invoer afwijken. De
              betaallink wordt meteen ververst.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
